import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { MappedLink } from "@MasterBluePrint/MobiusOverrides/Menu/Menu";
import { products } from "@MasterBluePrint/products";
import { getCategoryNavJson } from "@MasterBluePrint/Services/CategoryNavJsonService";
import themeColors, { themeFontSizes } from "@MasterBluePrint/Styles/variables";
import isMobile from "ismobilejs";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";

interface OwnProps {
    megaMenuLinks?: MappedLink | undefined;
    isHomePage?: boolean;
    viewAll?: boolean;
}

export interface GlobalProductMenuStyles {
    menuWrapper?: InjectableCss;
    productMenuHeader?: InjectableCss;
    productMenuUnderline?: InjectableCss;
    productMenuItemsWrapper?: InjectableCss;
    productMenuItem?: InjectableCss;
    activeProductMenuItem?: InjectableCss;
    productMenuItemUnderline?: InjectableCss;
    productMenuSideBar?: InjectableCss;
    productMenuActiveSideBar?: InjectableCss;
    linkWrapper?: InjectableCss;
    activeLinkWrapper?: InjectableCss;
    category?: InjectableCss;
    categoryMenuHeader?: InjectableCss;
    categoryMenuItem?: InjectableCss;
    categoryMenuItemIcon?: InjectableCss;
    tested?: InjectableCss;
    categoryLinkWrapper?: InjectableCss;
    image?: InjectableCss;
    linkText?: InjectableCss;
    menutriangle?: InjectableCss;
    button?: InjectableCss;
    buttonItems?: InjectableCss;
    buttonIcon?: InjectableCss;
    activeButtonIcon?: InjectableCss;
    redUnderline?: InjectableCss;
    categoryMenuHeaderTypography?: TypographyPresentationProps;
    activeProductMenuItemForHome?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        websiteName: state.context.website?.name,
    };
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

type Child = {
    url: string | undefined;
    title: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined;
};

export const GlobalProductMenuStyles: GlobalProductMenuStyles = {
    menuWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            height: 100%;
            z-index: 1;
            background-color: ${themeColors.colorWhite};

            &:after {
                background: rgba(0, 37, 84, .6);
            }
        `,
    },
    categoryMenuHeaderTypography: {
        css: css`
            align-self: center;
            color: ${themeColors.colorPrimary};
            font-size: ${themeFontSizes.size16};
            font-weight: 500;
            letter-spacing: 0;
            line-height: 1.1875rem;
            margin-left: .9375rem;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    productMenuUnderline: {
        css: css`
            height: .0625rem;
            width: 17rem;
            opacity: .51;
            background-color: ${themeColors.colorBorderBrands};
        `,
    },
    productMenuItemsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
            min-width: 17.0625rem;
            max-width: 17.0625rem;
            padding-top: 1.5rem;
            background: ${themeColors.colorWhite};
            padding-left: .3rem;
            padding-right: .3rem;
            padding-top: 0;
            box-shadow: none;
            z-index: 1;
            
            &:hover {
                border-right: solid 2px rgba(0, 0, 0, .2);
            }
        `,
    },
    productMenuItemUnderline: {
        css: css`
            height: .0625rem;
            width: 16.25rem;
            opacity: .51;
            background-color: ${themeColors.colorBorderBrands};
        `,
    },
    productMenuItem: {
        css: css`
            height: auto;
            width: 100%;
            color: ${themeColors.colorPrimary};
            font-size: ${themeFontSizes.size14};
            letter-spacing: -.014rem;
            line-height: inherit;
            padding-left: 1rem;
            padding-right: .5rem;
            margin-right: .5rem;
            white-space: normal;
            text-transform: capitalize;
            font-weight: normal;
            position: relative;
            min-width: 15.438rem;
            padding-top: .5rem;
            padding-bottom: .5rem;
            > span {
                color: ${themeColors.colorPrimary} !important;
            }
        `,
    },
    activeProductMenuItem: {
        css: css`
            height: auto;
            width: 100%;
            color: ${themeColors.colorActiveMenuLink};
            font-weight: 500;
            font-size: ${themeFontSizes.size14};
            letter-spacing: -.014rem;
            line-height: inherit;
            padding-left: 1rem;
            padding-right: .5rem;
            margin-right: .5rem;
            white-space: normal;
            text-transform: capitalize;
            position: relative;
            min-width: 15.4375rem;
            padding-top: .5rem;
            padding-bottom: .5rem;

            &:after,
            &:before {
                content: "";
                position: absolute;
                top: 0;
                left: auto;
                right: -.938rem;
                width: 0;
                z-index: 9;
                height: 0;
                border-bottom: 1.125rem solid transparent;
                border-top: 1.125rem solid transparent;
                border-right: 0.625rem solid ${themeColors.colorWhite};
            }
            &:before {
                left: auto;
                right: -.8125rem;
                border-bottom: 1.125rem solid transparent;
                border-top: 1.125rem solid transparent;
                border-right: 0.625rem solid rgba(51,63,72,0.09);
            }
            > span {
                color: ${themeColors.colorActiveMenuLink} !important;
            }
        `,
    },
    activeProductMenuItemForHome: {
        css: css`
            height: auto;
            width: 100%;
            color: ${themeColors.colorActiveMenuLink};
            font-weight: 500;
            font-size: ${themeFontSizes.size14};
            letter-spacing: -.014rem;
            line-height: inherit;
            padding-left: 1rem;
            padding-right: .5rem;
            margin-right: .5rem;
            white-space: nowrap;
            text-transform: capitalize;
            position: relative;
            min-width: 15.4375rem;
        `,
    },
    productMenuActiveSideBar: {
        css: css`
            background-color: ${themeColors.colorPrimary};
            height: auto;
            width: .25rem;
        `,
    },
    productMenuSideBar: {
        css: css`
            height: auto;
            width: .25rem;
        `,
    },
    linkWrapper: {
        css: css`
            height: auto;
            width: 100%;
            display: flex;
            flex-direction: row;
            align-items: center;
            border-bottom: 1.2px solid ${themeColors.colorBorderNav};
        `,
    },
    activeLinkWrapper: {
        css: css`
            background-color: ${themeColors.colorMenuHeader};
            color: ${themeColors.colorActiveMenuLink};
            display: flex;
            flex-direction: row;
            font-size: ${themeFontSizes.size14};
            font-weight: 500;
            height: auto;
            width: 100%;
            letter-spacing: -.014rem;
            line-height: inherit;
            text-transform: capitalize;
            border-bottom: 1.2px solid ${themeColors.colorBorderNav};
        `,
    },
    category: {
        css: css`
            z-index: 999;
            background: ${themeColors.colorWhite};
            min-width: 50.9375rem;
            left: 0;
            position: relative;

            @media (max-width: 1200px) and (max-width: 1024px) {
                min-width: 40.625rem;
            }
        `,
    },
    categoryMenuHeader: {
        css: css`
            min-width: 100%;
            height: 6.25rem;
            background-color: ${themeColors.colorMenuHeader};
            display: flex;
            font-weight: bold;
            padding-left: 1.5rem;
            position: relative;
            z-index: 9;
        `,
    },
    tested: {
        css: css`
            background-color: ${themeColors.colorBlack};
        `,
    },
    menutriangle: {
        css: css`
            width: 0;
            height: 0;
            border: 20px solid transparent;
            border-right-color: ${themeColors.colorWhite};
            left: 30.625rem;
            z-index: 3;
            display: flex;
            display: none;
        `,
    },
    categoryLinkWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 17%;
            margin: .625rem;
            border: 1px solid rgba(0, 37, 84, .1);
            border-radius: .125rem;
            box-sizing: border-box;
            align-items: center;
            justify-content: flex-start;
            @media (max-width: 1200px) and (max-width: 1024px) {
                width: 15%;
            }
        `,
    },
    categoryMenuItem: {
        css: css`
            display: flex;
            justify-content: space around;
            flex-wrap: wrap;
            padding: 1.5rem;
            position: relative;
            z-index: 9;
            background: ${themeColors.colorWhite};
        `,
    },
    image: {
        css: css`
            text-align: center;
            color: ${themeColors.colorPrimary};
            height: 70%;
            width: 100%;
            padding: .9375rem;
        `,
    },
    linkText: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${themeColors.colorPrimary};
            text-align: center;
            font-size: ${themeFontSizes.size15};
            height: 100%;
            padding: .5rem .7rem;
            width: 100%;
            overflow: hidden;
            font-weight: 500;
            text-transform: capitalize;
            line-height: 1.2rem;
            border-bottom: 4px solid ${themeColors.colorPrimary};
            > span {
                width: 0;
                height: 0;
            }
            &:hover {
                text-align: center;
                background: ${themeColors.colorPrimary};
                color: ${themeColors.colorYellow};
                > span {
                    width: 1.25rem;
                    height: 1.25rem;
                }
            }
        `,
    },
    button: {
        css: css`
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-around;
            height: 2.5rem;
            border-radius: .125rem;
            background: transparent;
            color: ${themeColors.colorPrimary};
            width: 18.75rem;
            border: 1px solid ${themeColors.colorPrimary};
            margin: 0 auto 1.875rem;
            text-transform: none;
            font-size: ${themeFontSizes.size14};
            &:hover {
                background: ${themeColors.colorPrimary};
                color: ${themeColors.colorWhite};
            }
        `,
    },
    buttonItems: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    buttonIcon: {
        css: css`
            margin-left: .625rem;
            font-size: ${themeFontSizes.size20};
            width: 1.25rem;
            height: 1.25rem;
        `,
    },
    activeButtonIcon: {
        css: css`
            margin-left: .625rem;
            color: ${themeColors.colorWhite};
            font-size: ${themeFontSizes.size20};
            width: 1.25rem;
            height: 1.25rem;
        `,
    },
    redUnderline: {
        css: css`
            height: .1875rem;
            width: 1.25rem;
            background-color: ${themeColors.colorRed};
            margin-top: .625rem;
        `,
    },
    categoryMenuItemIcon: {
        css: css`
            margin-left: .31250625rem;
            color: ${themeColors.colorWhite};
        `,
    },
};

const styles = GlobalProductMenuStyles;

const GlobalProductMenu: FC<Props> = ({ websiteName, megaMenuLinks, isHomePage, viewAll }) => {
    let featureFlags: string[] = ["TEUS-8214"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isCatImageVisibleOnDesktop = flagValues["TEUS-8214"] && !isMobileDevice();

    const [productsList, setProductsList] = useState({
        activeItem: {
            title: "",
            url: "",
            children: [] as any,
        },
        products: megaMenuLinks?.children,
    });

    const [show, setShow] = useState(false);
    useEffect(() => {
        setProductsList({
            ...productsList,
            products: megaMenuLinks?.children,
        });
    }, [megaMenuLinks]);

    const [isViewAll, setViewAll] = useState(viewAll);
    const [viewAllItems, setViewAllItems] = useState(false);
    const [activeButtonIcon, setActiveButtonIcon] = useState(false);
    const ref = useRef(null);



    const [productsImageData, setProductsImageData] = useState();

    useEffect(() => {
        getCategoryNavJson().then(response => {
            const jsondata = response["categoryNavJson"][0]["jsondata"];
            setProductsImageData(JSON.parse(jsondata));
        });
    }, []);

    const productImages = productsImageData || products;

    let productImageArr: any = [];
    if (productsList.activeItem) {
        const activeProductImage = productImages.find(
            (activeImage: any) => activeImage.title === productsList.activeItem.title && activeImage.WebSiteName === websiteName,
        );
        productImageArr = activeProductImage?.list.map((item: any) => {
            return {
                value: item.img,
                text: item.text,
            };
        });
    }
    const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !(ref.current! as any).contains(event.target)) {
            setTimeout(() => {
                setShow(false);
                // setViewAll(false);
                inActive();
            });
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    });
    useEffect(() => {
        const mainEle = document.getElementsByTagName("main")[0];
        const breadCrumbEle = document.querySelector('[data-test-selector="pageBreadcrumbs"]');
        const navStyleWrapper = document.querySelector('.nav-style-wrapper');

        if (!show) {
            mainEle?.classList.remove("active-nav");
            breadCrumbEle?.classList.remove("active-navs");
            navStyleWrapper?.classList.remove("active-arrow");
        } else if (show) {
            mainEle?.classList.add("active-nav");
            breadCrumbEle?.classList.add("active-navs");
            navStyleWrapper?.classList.add("active-arrow");
        }
    }, [show]);
    useEffect(() => {
        if (!isHomePage) {
            isActive(0);
            setShow(true);
        }
        setViewAll(true);
    }, []);

    const isActive = (index: number) => {
        setProductsList({
            ...productsList,
            activeItem: {
                title: productsList.products![index].title,
                url: productsList.products![index].url ?? "",
                children: productsList.products![index].children,
            },
        });
    };

    const inActive = () => {
        setProductsList({
            ...productsList,
            activeItem: {
                title: "",
                url: "",
                children: [],
            },
        });
    };

    const toggleActiveSidebar = (index: number) => {
        if (productsList.products![index].title === productsList.activeItem.title) {
            return { ...styles.productMenuActiveSideBar };
        }

        return { ...styles.productMenuSideBar };
    };

    const toggleActiveProduct = (index: number) => {
        if (productsList.products![index].title === productsList.activeItem.title) {
            return { ...styles.activeLinkWrapper };
        }
        return { ...styles.linkWrapper };
    };

    const toggleActiveProductText = (index: number) => {
        if (productsList.products![index].title === productsList.activeItem.title) {
            return { ...styles.activeProductMenuItem };
        }
        return { ...styles.productMenuItem };
    };

    const toggleActiveProductCategory = (index: number) => {
        if (productsList.products![index].title === productsList.activeItem.title) {
            return { ...styles.menutriangle };
        }
    };

    const toggleMenuItem = (index: number) => {
        isActive(index);
        setShow(true);
    };

    const toggleCategoryMenu = () => {
        inActive();
        setShow(false);
        if (!isHomePage) {
            setViewAll(false);
        } else {
            setViewAll(true);
        }
    };

    return (
        <>
            {isViewAll && (
                <StyledWrapper {...styles.menuWrapper} ref={ref} onMouseLeave={() => toggleCategoryMenu()}>
                    <StyledWrapper {...styles.productMenuItemsWrapper} className="prod-items-wrapper">
                        {productsList.products &&
                            productsList.products!.map((product: any, index: any) => (
                                <StyledWrapper key={index}>
                                    <StyledWrapper
                                        {...toggleActiveProduct(index)}
                                        onMouseEnter={() => toggleMenuItem(index)}
                                    >
                                        <StyledWrapper {...toggleActiveSidebar(index)}></StyledWrapper>
                                        <Link 
                                            href={product.url} 
                                            {...toggleActiveProductText(index)} 
                                            onMouseDown={(prodCatLink) => {
                                                if (prodCatLink.button === 0 && !prodCatLink.ctrlKey && !prodCatLink.metaKey) {
                                                    window.location.href = product.url;
                                                }
                                            }}
                                            onClick={(prodCatLink) => {
                                                if (!prodCatLink.ctrlKey && !prodCatLink.metaKey) {
                                                    prodCatLink.preventDefault();
                                                    prodCatLink.stopPropagation();
                                                }
                                            }}
                                        >
                                            {product.title}
                                        </Link>
                                        <StyledWrapper {...toggleActiveProductCategory(index)}></StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>

                    {show && productsList.activeItem && (
                        <StyledWrapper {...styles.category}>
                            <StyledWrapper {...styles.categoryMenuHeader}>
                                <Typography {...styles.categoryMenuHeaderTypography}>
                                    {productsList.activeItem.title}
                                    <StyledWrapper {...styles.redUnderline}></StyledWrapper>
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.categoryMenuItem}>
                                {viewAllItems
                                    ? productsList.activeItem.children?.map((child: Child, index: number) => (
                                        <Clickable
                                            key={`view-all-${index}`}
                                            {...styles.categoryLinkWrapper}
                                            href={child.url}
                                            // onClick={toggleCategoryMenu}
                                            className="menu-lnk-category"
                                        >
                                            {!isCatImageVisibleOnDesktop && productImageArr && (
                                                <Img {...styles.image} altText={`${child?.title?.toString()} - image`} src={productImageArr[productImageArr.findIndex((item: any) => item.text === child.title)]?.value} />
                                            )}

                                            <StyledWrapper {...styles.linkText}>{child.title}</StyledWrapper>
                                        </Clickable>
                                    ))
                                    : productsList.activeItem.children
                                        ?.slice(0, 9)
                                        .map((child: Child, index: number) => (
                                            <Clickable
                                                key={`slice-${index}`}
                                                {...styles.categoryLinkWrapper}
                                                href={child.url}
                                                // onClick={toggleCategoryMenu}
                                                className="menu-lnk-category"
                                            >
                                                {!isCatImageVisibleOnDesktop && productImageArr && (
                                                    <Img  {...styles.image} altText={`${child?.title?.toString()} - image`} src={productImageArr[productImageArr.findIndex((item: any) => item.text === child.title)]?.value} />
                                                )}
                                                <StyledWrapper {...styles.linkText}>{child.title}</StyledWrapper>
                                            </Clickable>
                                        ))}
                            </StyledWrapper>
                            {!viewAllItems && productsList.activeItem.children.length > 10 && (
                                <StyledWrapper
                                    {...styles.button}
                                    onClick={() => setViewAllItems(true)}
                                    onMouseEnter={() => setActiveButtonIcon(true)}
                                    onMouseLeave={() => setActiveButtonIcon(false)}
                                >
                                    <StyledWrapper {...styles.buttonItems}>
                                        <Clickable href={productsList.activeItem.url}>
                                            <Typography>{translate("View all")}</Typography>
                                        </Clickable>
                                        {activeButtonIcon ? (
                                            <Icon src={ArrowRight} {...styles.activeButtonIcon} />
                                        ) : (
                                            <Icon src={ArrowRight} {...styles.buttonIcon} />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            {/* {(viewAll && productsList.activeItem.children.length > 10) && (
                    <StyledWrapper {...styles.button} onClick={() => setViewAll(false)} onMouseEnter={() => setActiveButtonIcon(true)} onMouseLeave={() => setActiveButtonIcon(false)}>
                        <StyledWrapper {...styles.buttonItems}>
                            <Typography>{translate("Collapse All")}</Typography>
                            {activeButtonIcon ? <Icon src={ArrowLeft} {...styles.activeButtonIcon} /> : <Icon src={ArrowLeft} {...styles.buttonIcon} />}
                        </StyledWrapper>
                    </StyledWrapper>
                )} */}
                        </StyledWrapper >
                    )}
                </StyledWrapper >
            )}
        </>
    );
};
export default connect(mapStateToProps)(GlobalProductMenu);